import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';

export default function SaveSettingsDialog({
  open,
  onClose,
  onSave,
  savedConfig,
  editedConfig,
  changedKeys,
  saving,
  saveResult,
  getSettingLabel,
}) {
  const formatValue = (value) => {
    if (typeof value === 'boolean') return value ? 'On' : 'Off';
    return String(value);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Settings?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The following changes will be applied to your sensor:
        </Typography>

        {changedKeys.map((key) => (
          <Box key={key} sx={{ mb: 2, pl: 1, borderLeft: 2, borderColor: 'primary.main' }}>
            <Typography variant="body2" fontWeight="bold">
              {getSettingLabel(key)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {formatValue(savedConfig[key])}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                &rarr;
              </Typography>
              <Typography variant="body2" color="primary">
                {formatValue(editedConfig[key])}
              </Typography>
            </Box>
          </Box>
        ))}

        {saving && <LinearProgress sx={{ mt: 2 }} />}

        {saveResult?.success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Settings saved successfully.
          </Alert>
        )}

        {saveResult?.success === false && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveResult.error || 'Failed to save some settings.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {saveResult ? 'Close' : 'Cancel'}
        </Button>
        {!saveResult && (
          <Button onClick={onSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

SaveSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  savedConfig: PropTypes.object.isRequired,
  editedConfig: PropTypes.object.isRequired,
  changedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  saving: PropTypes.bool.isRequired,
  saveResult: PropTypes.shape({
    success: PropTypes.bool,
    error: PropTypes.string,
  }),
  getSettingLabel: PropTypes.func.isRequired,
};
